/* eslint-disable react-hooks/exhaustive-deps */
'use client'

import variables from '@/app/src/app/scss/abstracts/export.module.scss'

import { IStickyLink } from '../model'
import { parseAnchors, createNavigation, checkTop, checkDirection } from '../lib'
import { createContext, useContext, useEffect, useState } from 'react'

import { scrollToId } from '@/app/src/shared/lib/scrollTo'
import { useRouter } from 'next/navigation'
import type { Route } from 'next'
import { usePathname } from 'next/navigation'

const {
  headerHeadroomWithStickyNavigation,
  headerMobWithStickyNavigationMod,
  stickyNavigationHeight,
  breakpointXl,
} = variables

const StickyNavigation = createContext<{
  isInit: boolean
  navigation: IStickyLink[]
  setActiveId: (el: HTMLDivElement | null, id: string) => void
  scrollToId: (id: string) => void
}>({
  isInit: false,
  navigation: [],
  setActiveId: (): void => {},
  scrollToId: (): void => {},
})

export const StickyNavigationProvider = ({ children }: { children: React.ReactNode }) => {
  // const anchors = parseAnchors()
  const [anchors, setAnchors] = useState(parseAnchors())
  const navHeight = parseInt(stickyNavigationHeight) - 2
  const navWithHeadroomHeight = parseInt(headerHeadroomWithStickyNavigation) - 2
  const headerMobWithNavMobHeight = parseInt(headerMobWithStickyNavigationMod) - 2

  const router = useRouter()
  const pathname = usePathname()
  const [navigation, setNavigation] = useState<IStickyLink[]>([])

  useEffect(() => {
    setAnchors(parseAnchors())
    setNavigation([])
  }, [pathname])

  const setActiveId = (id: string) => {
    const currentMenuItem = navigation.find((item) => item.id === id)

    router.push((currentMenuItem?.href ?? '') as Route, { scroll: false })

    const updatedNavigation = navigation.map((item) => ({
      ...item,
      isActive: id === item.id,
    }))

    setNavigation(updatedNavigation)
  }

  const handleSetActiveId = (el: HTMLDivElement | null, id: string) => {
    const isTop = checkTop(el)

    if (isTop) setActiveId(id)
  }

  const handleScrollToId = (id: string) => {
    const prevNavItemIndex = navigation.findIndex((item) => item.isActive)
    const currentNavItemIndex = navigation.findIndex((item) => item.id === id)

    const direction = checkDirection(prevNavItemIndex, currentNavItemIndex)

    if (direction === 'not-changed') return

    const cssVal = direction === 'to-bottom' ? `${navHeight}px` : `${navWithHeadroomHeight}px`

    document.documentElement.style.scrollPaddingTop = cssVal

    scrollToId(id)
  }

  useEffect(() => {
    if (!navigation.length) {
      setNavigation(createNavigation(anchors))
    }

    return () => {
      document.documentElement.style.scrollPaddingTop = ''
    }
  }, [anchors])

  return (
    <StickyNavigation.Provider
      value={{
        navigation,
        setActiveId: handleSetActiveId,
        scrollToId: handleScrollToId,
        isInit: !!navigation.length,
      }}
    >
      <style
        dangerouslySetInnerHTML={{
          __html: `
            html {
              scroll-padding-top: ${navHeight}px;

              @media(max-width: ${breakpointXl}) {
                scroll-padding-top: ${headerMobWithNavMobHeight}px !important;
              }
            }
          `,
        }}
      />
      {children}
    </StickyNavigation.Provider>
  )
}

export const useStickyNavigationContext = () => useContext(StickyNavigation)
